import { useState, useEffect } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Search, ShieldCheck, Trash2, TriangleAlert, Check, Loader2, BookOpenCheck, Eye, EyeOff, Download, Archive, ArchiveRestore, Layers, Shuffle } from "lucide-react";
import { ExpertsManager } from "@/components/admin/ExpertsManager";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const blockRow = (perBlock) => [1, 2, 3, 4].map((b) => `B${b}: ${perBlock?.[String(b)] ?? 0}`).join(" · ");

/** Sezione contenuti riutilizzabile: import schede tipizzate di un modulo + pubblicazione. */
const CardsModuleSection = ({ moduleKey, title, description }) => {
  const [importing, setImporting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [report, setReport] = useState(null);

  const isPublished = report && Object.values(report.lessonStatuses || {}).every((s) => s === "published");

  const runImport = async () => {
    setImporting(true);
    try {
      const r = await api.post(`/admin/maintenance/cards/${moduleKey}/import`);
      setReport(r.data);
      toast.success(`Import completato: ${r.data.lessonsReplaced} lezioni, ${r.data.cardsReplaced} schede.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setImporting(false);
    }
  };

  const setStatus = async (status) => {
    setPublishing(true);
    try {
      const r = await api.post(`/admin/maintenance/cards/${moduleKey}/status`, { status });
      toast.success(status === "published" ? `Pubblicate ${r.data.lessons} lezioni: ora visibili agli studenti.` : `${r.data.lessons} lezioni riportate in bozza (solo admin).`);
      setReport((prev) =>
        prev
          ? { ...prev, lessonStatuses: Object.fromEntries(Object.keys(prev.lessonStatuses || {}).map((k) => [k, status])) }
          : prev
      );
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5" data-testid={`admin-cards-card-${moduleKey}`}>
      <div className="flex items-center gap-2">
        <BookOpenCheck className="w-4 h-4" aria-hidden="true" />
        <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase">{title}</h3>
      </div>
      <p className="mt-2 text-xs text-[color:var(--text-2)] leading-5">
        {description}
        {" "}Le lezioni importate nascono in <strong>bozza</strong>: visibili solo agli admin finché non le pubblichi.
        Non tocca simulazioni, paywall né gli altri moduli.
      </p>

      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          onClick={runImport}
          disabled={importing || publishing}
          data-testid={`admin-cards-import-button-${moduleKey}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
        >
          {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {importing ? "Import…" : "Importa / aggiorna schede"}
        </button>
        {report && (
          isPublished ? (
            <button
              onClick={() => setStatus("draft")}
              disabled={publishing || importing}
              data-testid={`admin-cards-unpublish-button-${moduleKey}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-[color:var(--bg-subtle)] disabled:opacity-60 transition-colors duration-200"
            >
              {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
              Riporta in bozza
            </button>
          ) : (
            <button
              onClick={() => setStatus("published")}
              disabled={publishing || importing}
              data-testid={`admin-cards-publish-button-${moduleKey}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:brightness-95 disabled:opacity-60 transition-all duration-200"
            >
              {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
              Pubblica agli studenti
            </button>
          )
        )}
      </div>

      {report && (
        <div className="mt-4 rounded-2xl border border-[color:var(--hairline)] bg-white p-4 text-sm space-y-2" data-testid={`admin-cards-report-${moduleKey}`}>
          <p className="flex items-center gap-2 font-bold">
            <Check className="w-4 h-4" strokeWidth={3} /> {report.lessonsReplaced} lezioni · {report.cardsReplaced} schede importate
          </p>
          {report.legacyDeleted?.length > 0 && (
            <p className="text-xs text-[color:var(--text-2)]">
              Lezioni legacy rimosse: {report.legacyDeleted.join(", ")}
            </p>
          )}
          {report.unknownTypes?.length > 0 && (
            <p className="flex items-start gap-2 text-xs font-bold" style={{ color: "#B45309" }}>
              <TriangleAlert className="w-4 h-4 shrink-0" /> Tipi scheda non riconosciuti (ignorati): {report.unknownTypes.join(", ")}
            </p>
          )}
          <div className="text-xs space-y-1 pt-2 border-t border-[color:var(--hairline)]">
            {Object.entries(report.lessonStatuses || {}).map(([slug, st]) => (
              <p key={slug} className="font-mono flex items-center justify-between gap-2">
                <span className="truncate">{slug}{report.lessonCards ? ` · ${report.lessonCards[slug] ?? 0} schede` : ""}</span>
                <span className={`shrink-0 text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2 py-0.5 ${st === "published" ? "bg-[color:var(--volt)]" : "text-white"}`} style={st === "published" ? undefined : { background: "#B45309" }}>
                  {st === "published" ? "Pubblicata" : "Bozza"}
                </span>
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** Archivio moduli: nascondi/ripristina un modulo agli studenti (reversibile, nessuna cancellazione). */
const BLOCK_LABEL = { 1: "Blocco 1", 2: "Blocco 2", 3: "Blocco 3", 4: "Blocco 4" };

const ModuleVisibilitySection = () => {
  const [modules, setModules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/modules");
      setModules(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (m) => {
    setBusyId(m.id);
    try {
      const r = await api.put(`/admin/modules/${m.id}/visibility`, { hidden: !m.hidden });
      setModules((prev) => prev.map((x) => (x.id === m.id ? { ...x, hidden: r.data.hidden } : x)));
      toast.success(r.data.hidden ? `"${m.titolo}" archiviato: nascosto agli studenti.` : `"${m.titolo}" ripristinato: visibile agli studenti.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5" data-testid="admin-module-visibility-card">
      <div className="flex items-center gap-2">
        <Layers className="w-4 h-4" aria-hidden="true" />
        <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase">Archivio moduli — visibilità agli studenti</h3>
      </div>
      <p className="mt-2 text-xs text-[color:var(--text-2)] leading-5">
        Nascondi (archivia) o ripristina un modulo per gli studenti. È <strong>reversibile</strong> e non elimina nulla:
        lezioni, domande e simulazioni restano nel database. Il pool domande per blocco non viene mai toccato.
      </p>

      {loading && !modules && (
        <p className="mt-4 flex items-center gap-2 text-sm text-[color:var(--text-2)]"><Loader2 className="w-4 h-4 animate-spin" /> Carico i moduli…</p>
      )}

      {modules && (
        <div className="mt-4 space-y-2" data-testid="admin-module-visibility-list">
          {modules.map((m) => (
            <div
              key={m.id}
              data-testid={`admin-module-row-${m.id}`}
              className="flex items-center justify-between gap-3 rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-wide text-[color:var(--text-2)]">{BLOCK_LABEL[m.blocco] || `Blocco ${m.blocco}`}</span>
                  {m.hidden ? (
                    <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2 py-0.5 text-white" style={{ background: "#B45309" }}>Archiviato</span>
                  ) : (
                    <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2 py-0.5 bg-[color:var(--volt)] text-[color:var(--ink)]">Visibile</span>
                  )}
                </div>
                <p className="text-sm font-bold truncate mt-0.5">{m.titolo}</p>
                <p className="font-mono text-[11px] text-[color:var(--text-2)]">
                  {m.lessonCounts?.published || 0} pubbl. · {m.lessonCounts?.draft || 0} bozza
                </p>
              </div>
              <button
                onClick={() => toggle(m)}
                disabled={busyId === m.id}
                data-testid={`admin-module-toggle-${m.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full text-xs font-extrabold px-3.5 py-2 min-h-[36px] shrink-0 disabled:opacity-60 transition-colors duration-200 border ${
                  m.hidden
                    ? "bg-[color:var(--ink)] text-white border-transparent hover:bg-black/85"
                    : "bg-white text-[color:var(--ink)] border-[color:var(--hairline)] hover:bg-[color:var(--bg-subtle)]"
                }`}
              >
                {busyId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : m.hidden ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                {m.hidden ? "Ripristina" : "Archivia"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Esperti / Garanti: crea la collezione 'experts' e assegna le aree ai moduli (idempotente). */
const ExpertsSeedSection = () => {
  const [seeding, setSeeding] = useState(false);
  const [report, setReport] = useState(null);

  const runSeed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/admin/experts/seed");
      setReport(r.data);
      toast.success(`Esperti sincronizzati: ${r.data.activeExperts} attivo/i. Moduli aggiornati: ${r.data.modulesUpdated.length}.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5" data-testid="admin-experts-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" aria-hidden="true" />
        <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase">Esperti / Garanti — sincronizza</h3>
      </div>
      <p className="mt-2 text-xs text-[color:var(--text-2)] leading-5">
        Crea (o aggiorna) la collezione degli esperti che validano i contenuti e assegna le aree ai moduli.
        Operazione <strong>idempotente</strong>: ripetibile senza duplicati. Da eseguire una volta in produzione dopo il deploy.
        Non tocca lezioni, simulazioni, paywall né altri moduli.
      </p>

      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          onClick={runSeed}
          disabled={seeding}
          data-testid="admin-experts-seed-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
        >
          {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {seeding ? "Sincronizzo…" : "Crea / aggiorna esperti"}
        </button>
      </div>

      {report && (
        <div className="mt-4 rounded-2xl border border-[color:var(--hairline)] bg-white p-4 text-sm space-y-2" data-testid="admin-experts-report">
          <p className="flex items-center gap-2 font-bold">
            <Check className="w-4 h-4" strokeWidth={3} /> {report.activeExperts} esperto/i attivo/i · {report.created ? "creato" : "aggiornato"}
          </p>
          <p className="text-xs text-[color:var(--text-2)]">
            Moduli con badge (aree assegnate): {report.modulesUpdated.length ? report.modulesUpdated.join(", ") : "nessuno"}
          </p>
        </div>
      )}
    </div>
  );
};

/** Riequilibrio posizione risposta corretta A/B/C/D nei dati salvati (idempotente, con backup). */
const letters = ["A", "B", "C", "D"];
const distRow = (obj) => letters.map((l, i) => `${l}:${obj?.[String(i)] ?? 0}`).join(" · ");

const OptionsBalanceSection = () => {
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const analyze = async () => {
    setAnalyzing(true);
    setResult(null);
    try {
      const r = await api.get("/admin/maintenance/options-balance");
      setReport(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const runApply = async () => {
    setConfirmOpen(false);
    setApplying(true);
    try {
      const r = await api.post("/admin/maintenance/options-balance/apply", { confirm: "RIEQUILIBRA", includeSample: true });
      setResult(r.data);
      setReport(null);
      toast.success(`Riequilibrate ${r.data.updatedQuestions} domande + ${r.data.updatedSample} del trial.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setApplying(false);
    }
  };

  const v = result?.verification;

  return (
    <div className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5" data-testid="admin-balance-card">
      <div className="flex items-center gap-2">
        <Shuffle className="w-4 h-4" aria-hidden="true" />
        <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase">Manutenzione — Riequilibrio risposte corrette</h3>
      </div>
      <p className="mt-2 text-xs text-[color:var(--text-2)] leading-5">
        Riordina le opzioni delle domande così che la risposta corretta cada circa il <strong>25%</strong> delle volte su A/B/C/D in ogni blocco.
        Cambia <strong>solo l'ordine delle opzioni</strong> (testo, spiegazione, blocco, modulo e difficoltà restano intatti). Viene creato un backup automatico.
        Nota: durante esami e simulazioni le opzioni vengono <strong>comunque</strong> rimescolate ad ogni tentativo — questo pulisce i dati salvati (export, editor).
      </p>

      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          onClick={analyze}
          disabled={analyzing || applying}
          data-testid="admin-balance-analyze-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
        >
          {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {analyzing ? "Analisi…" : "1. Analizza (anteprima)"}
        </button>
        {report && (
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={applying}
            data-testid="admin-balance-apply-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:brightness-95 disabled:opacity-60 transition-all duration-200"
          >
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shuffle className="w-3.5 h-3.5" />}
            2. Riequilibra ora
          </button>
        )}
      </div>

      {/* Anteprima prima/dopo */}
      {report && !result && (
        <div className="mt-4 rounded-2xl border border-[color:var(--hairline)] bg-white p-4 text-sm space-y-2" data-testid="admin-balance-report">
          {[1, 2, 3, 4].map((b) => {
            const row = report.questions?.[String(b)];
            if (!row) return null;
            return (
              <div key={b} className="pt-2 first:pt-0 border-t first:border-t-0 border-[color:var(--hairline)]" data-testid={`admin-balance-preview-block-${b}`}>
                <p className="font-bold text-xs">Blocco {b} <span className="font-mono text-[color:var(--text-2)]">({row.total} domande)</span></p>
                <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-0.5">Prima: {distRow(row.before)}</p>
                <p className="font-mono text-[11px] mt-0.5">Dopo: <span className="font-bold">{distRow(row.after)}</span></p>
              </div>
            );
          })}
          {report.sampleQuestions && (
            <p className="font-mono text-[11px] text-[color:var(--text-2)] pt-2 border-t border-[color:var(--hairline)]">
              Trial incluso ({[1, 2, 3, 4].reduce((s, b) => s + (report.sampleQuestions[String(b)]?.total || 0), 0)} domande)
            </p>
          )}
        </div>
      )}

      {/* Verifica post-operazione */}
      {result && (
        <div className="mt-4 rounded-2xl border border-[color:var(--hairline)] bg-white p-4 text-sm space-y-2" data-testid="admin-balance-result">
          <p className="flex items-center gap-2 font-bold" data-testid="admin-balance-done">
            <Check className="w-4 h-4" strokeWidth={3} /> Riequilibrate {result.updatedQuestions} domande{result.updatedSample ? ` + ${result.updatedSample} trial` : ""} (backup: <span className="font-mono text-xs">{result.backupId?.slice(0, 8)}…</span>)
          </p>
          {v && (
            <div className="text-xs space-y-1 pt-2 border-t border-[color:var(--hairline)]" data-testid="admin-balance-verification">
              {[1, 2, 3, 4].map((b) => (
                <p key={b} className="font-mono text-[11px]">
                  B{b}: {distRow(v.questions?.[String(b)]?.before)}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-extrabold tracking-tight">Riequilibrare le risposte corrette?</AlertDialogTitle>
            <AlertDialogDescription>
              Verrà riordinato l'ordine delle opzioni di tutte le domande (e del trial) per distribuire la risposta corretta ~25% su A/B/C/D.
              Non cambiano testi né spiegazioni. Viene creato un backup automatico nel database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="admin-balance-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={runApply}
              data-testid="admin-balance-confirm-button"
              className="rounded-full bg-[color:var(--ink)] hover:bg-black/85"
            >
              Riequilibra ora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const AdminMaintenance = () => {
  const [report, setReport] = useState(null); // analisi pre-eliminazione
  const [result, setResult] = useState(null); // esito post-eliminazione
  const [analyzing, setAnalyzing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const analyze = async () => {
    setAnalyzing(true);
    setResult(null);
    try {
      const r = await api.get("/admin/maintenance/example-questions");
      setReport(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const runCleanup = async () => {
    setConfirmOpen(false);
    setDeleting(true);
    try {
      const r = await api.post("/admin/maintenance/example-questions/cleanup", { confirm: "ELIMINA-DEFINITIVAMENTE" });
      setResult(r.data);
      setReport(null);
      toast.success(r.data.alreadyClean ? "Database già pulito: nessuna modifica." : `Eliminate ${r.data.deleted} domande di esempio.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleting(false);
    }
  };

  const v = result?.verification;

  return (
    <>
    <CardsModuleSection
      moduleKey="medicina"
      title="Contenuti — Modulo Medicina (schede tipizzate)"
      description="Importa/aggiorna le 4 lezioni con schede tipizzate del Blocco 1 dal file contenuti in dotazione all'app (idempotente: ripetibile senza duplicati)."
    />
    <CardsModuleSection
      moduleKey="regolamento"
      title="Contenuti — Modulo Regolamento di Giuoco (schede tipizzate)"
      description="Importa/aggiorna le 6 lezioni con schede tipizzate del Blocco 2 (Regole 1–17) dal file contenuti in dotazione all'app (idempotente: ripetibile senza duplicati)."
    />
    <CardsModuleSection
      moduleKey="carte"
      title="Contenuti — Modulo Carte Federali (schede tipizzate)"
      description="Importa/aggiorna le 5 lezioni con schede tipizzate del Blocco 3 (ordinamento FIGC, Statuto, Settore Tecnico, NOIF, giustizia sportiva) dal file contenuti in dotazione all'app (idempotente: ripetibile senza duplicati)."
    />
    <CardsModuleSection
      moduleKey="indirizzi"
      title="Contenuti — Modulo Indirizzi Tecnici (schede tipizzate)"
      description="Importa/aggiorna le 7 lezioni con schede tipizzate del Blocco 4 (preparazione, tecnica/tattica, femminile, futsal, portiere, AIAC, paralimpico) dal file contenuti in dotazione all'app (idempotente: ripetibile senza duplicati)."
    />
    <ModuleVisibilitySection />
    <ExpertsSeedSection />
    <ExpertsManager />
    <OptionsBalanceSection />
    <div className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5" data-testid="admin-maintenance-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" aria-hidden="true" />
        <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase">Manutenzione — Pulizia domande di esempio</h3>
      </div>
      <p className="mt-2 text-xs text-[color:var(--text-2)] leading-5">
        Rimuove definitivamente le domande di esempio (seed iniziale) dai pool: esame casuale, blocchi, moduli e ripasso errori.
        Le simulazioni ufficiali e le 20 domande del trial non vengono toccate. Prima analizza, poi conferma. Viene creato un backup automatico.
      </p>

      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          onClick={analyze}
          disabled={analyzing || deleting}
          data-testid="admin-cleanup-analyze-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
        >
          {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {analyzing ? "Analisi…" : "1. Analizza"}
        </button>
        {report && report.safeToDelete && (
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={deleting}
            data-testid="admin-cleanup-delete-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--danger)] text-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-[color:var(--danger)]/90 disabled:opacity-60 transition-colors duration-200"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            2. Elimina definitivamente ({report.exampleQuestions.total})
          </button>
        )}
      </div>

      {/* Analisi pre-eliminazione */}
      {report && !result && (
        <div className="mt-4 rounded-2xl border border-[color:var(--hairline)] bg-white p-4 text-sm space-y-2" data-testid="admin-cleanup-report">
          {report.alreadyClean ? (
            <p className="flex items-center gap-2 font-bold" data-testid="admin-cleanup-already-clean">
              <Check className="w-4 h-4" strokeWidth={3} /> Database già pulito: nessuna domanda di esempio trovata. Nulla da eliminare.
            </p>
          ) : (
            <>
              <p data-testid="admin-cleanup-found">
                Trovate <strong>{report.exampleQuestions.total} domande di esempio</strong>{" "}
                <span className="font-mono text-xs text-[color:var(--text-2)]">({blockRow(report.exampleQuestions.perBlock)})</span>
              </p>
              <p className="text-xs text-[color:var(--text-2)]">
                Domande reali che resteranno: <strong className="text-[color:var(--ink)]">{report.realQuestions.total}</strong>{" "}
                <span className="font-mono">({blockRow(report.realQuestions.perBlock)})</span>
              </p>
              {report.simulationDependencies.length > 0 ? (
                <p className="flex items-start gap-2 text-[color:var(--danger)] font-bold text-xs" data-testid="admin-cleanup-blocked">
                  <TriangleAlert className="w-4 h-4 shrink-0" />
                  BLOCCATO: {report.simulationDependencies.length} simulazioni ufficiali dipendono da domande di esempio ({report.simulationDependencies.map((s) => s.id).join(", ")}). Nessuna eliminazione possibile: contatta l'assistenza tecnica.
                </p>
              ) : (
                <p className="flex items-center gap-2 text-xs font-bold" data-testid="admin-cleanup-safe">
                  <Check className="w-4 h-4" strokeWidth={3} /> Simulazioni ufficiali: nessuna dipendenza — eliminazione sicura
                </p>
              )}
              <p className="font-mono text-[11px] text-[color:var(--text-2)]">
                Trial (non toccato): {report.sampleQuestions.total} domande ({blockRow(report.sampleQuestions.perBlock)})
              </p>
            </>
          )}
        </div>
      )}

      {/* Report di verifica post-eliminazione */}
      {result && (
        <div className="mt-4 rounded-2xl border border-[color:var(--hairline)] bg-white p-4 text-sm space-y-2" data-testid="admin-cleanup-result">
          {result.alreadyClean ? (
            <p className="flex items-center gap-2 font-bold">
              <Check className="w-4 h-4" strokeWidth={3} /> Database già pulito: nessuna modifica effettuata.
            </p>
          ) : (
            <p className="flex items-center gap-2 font-bold" data-testid="admin-cleanup-deleted-count">
              <Check className="w-4 h-4" strokeWidth={3} /> Eliminate {result.deleted} domande di esempio (backup: <span className="font-mono text-xs">{result.backupId?.slice(0, 8)}…</span>)
            </p>
          )}
          {v && (
            <div className="text-xs space-y-1.5 pt-2 border-t border-[color:var(--hairline)]" data-testid="admin-cleanup-verification">
              <p>
                Domande rimaste: <strong>{v.realQuestions.total}</strong>{" "}
                <span className="font-mono text-[color:var(--text-2)]">({blockRow(v.realQuestions.perBlock)})</span> · di esempio residue: <strong>{v.exampleQuestions.total}</strong>
              </p>
              <p>
                Simulazioni ufficiali:{" "}
                {v.simulations.map((s) => (
                  <span key={s.id} className={`font-mono mr-2 ${s.resolvable === s.totalQuestions && s.totalQuestions === 60 ? "" : "text-[color:var(--danger)] font-bold"}`}>
                    {s.id}: {s.resolvable}/60
                  </span>
                ))}
              </p>
              <p className="font-mono text-[color:var(--text-2)]">
                Trial intatto: {v.sampleQuestions.total} domande ({blockRow(v.sampleQuestions.perBlock)})
              </p>
            </div>
          )}
        </div>
      )}

      {/* Conferma esplicita */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-extrabold tracking-tight">
              Eliminare definitivamente {report?.exampleQuestions?.total} domande di esempio?
            </AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione è irreversibile dal pannello (viene creato un backup automatico nel database).
              Resteranno solo le {report?.realQuestions?.total} domande reali. Le simulazioni ufficiali e il trial non vengono toccati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="admin-cleanup-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={runCleanup}
              data-testid="admin-cleanup-confirm-button"
              className="rounded-full bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90"
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </>
  );
};
